import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as userVerificationApi from '../../api/userVerification';
import { message } from 'antd';

// 获取用户认证列表
export const fetchUserVerifications = createAsyncThunk(
  'userVerification/fetchList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await userVerificationApi.getVerificationList(params);
      if (response && response.code === 200) {
        // 返回数据和查询参数，以便在reducer中保存
        return {
          data: response.data,
          params
        };
      }
      return rejectWithValue(response?.message || '获取用户认证列表失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取用户认证列表失败');
    }
  }
);

// 获取用户认证详情
export const fetchUserVerificationDetail = createAsyncThunk(
  'userVerification/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await userVerificationApi.getVerificationDetail(id);
      if (response && response.code === 200) {
        return response.data;
      }
      return rejectWithValue(response?.message || '获取用户认证详情失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取用户认证详情失败');
    }
  }
);

// 审核用户认证
export const auditUserVerification = createAsyncThunk(
  'userVerification/audit',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await userVerificationApi.auditVerification(id, data);
      if (response && response.code === 200) {
        message.success('审核操作成功');
        return response.data;
      }
      return rejectWithValue(response?.message || '审核操作失败');
    } catch (error) {
      return rejectWithValue(error.message || '审核操作失败');
    }
  }
);

// 初始状态
const initialState = {
  // 用户认证列表
  verificationList: {
    data: [],
    total: 0,
    current: 1,
    size: 10,
    loading: false,
    error: null,
    searchParams: {
      status: undefined,
      userId: undefined,
      realName: undefined,
      idCardNumber: undefined
    }
  },
  // 用户认证详情
  currentVerification: {
    data: null,
    loading: false,
    error: null
  },
  // 审核状态
  auditStatus: {
    loading: false,
    success: false,
    error: null
  }
};

// 创建Slice
const userVerificationSlice = createSlice({
  name: 'userVerification',
  initialState,
  reducers: {
    clearAuditStatus: (state) => {
      state.auditStatus = {
        loading: false,
        success: false,
        error: null
      };
    },
    setSearchParams: (state, action) => {
      const { page, size, ...searchParams } = action.payload;
      
      // 更新分页参数
      if (page !== undefined) {
        state.verificationList.current = page;
      }
      if (size !== undefined) {
        state.verificationList.size = size;
      }
      
      // 更新搜索参数
      state.verificationList.searchParams = {
        ...state.verificationList.searchParams,
        ...searchParams
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // 处理获取用户认证列表
      .addCase(fetchUserVerifications.pending, (state) => {
        state.verificationList.loading = true;
        state.verificationList.error = null;
      })
      .addCase(fetchUserVerifications.fulfilled, (state, action) => {
        state.verificationList.loading = false;
        state.verificationList.data = action.payload.data.records || [];
        state.verificationList.total = action.payload.data.total || 0;
        state.verificationList.current = action.payload.data.current || 1;
        state.verificationList.size = action.payload.data.size || 10;
        
        // 保存搜索参数
        if (action.payload.params) {
          state.verificationList.searchParams = {
            ...state.verificationList.searchParams,
            ...action.payload.params
          };
        }
      })
      .addCase(fetchUserVerifications.rejected, (state, action) => {
        state.verificationList.loading = false;
        state.verificationList.error = action.payload;
        message.error(action.payload || '获取用户认证列表失败');
      })
      
      // 处理获取用户认证详情
      .addCase(fetchUserVerificationDetail.pending, (state) => {
        state.currentVerification.loading = true;
        state.currentVerification.error = null;
      })
      .addCase(fetchUserVerificationDetail.fulfilled, (state, action) => {
        state.currentVerification.loading = false;
        state.currentVerification.data = action.payload;
      })
      .addCase(fetchUserVerificationDetail.rejected, (state, action) => {
        state.currentVerification.loading = false;
        state.currentVerification.error = action.payload;
        message.error(action.payload || '获取用户认证详情失败');
      })
      
      // 处理审核用户认证
      .addCase(auditUserVerification.pending, (state) => {
        state.auditStatus.loading = true;
        state.auditStatus.success = false;
        state.auditStatus.error = null;
      })
      .addCase(auditUserVerification.fulfilled, (state) => {
        state.auditStatus.loading = false;
        state.auditStatus.success = true;
      })
      .addCase(auditUserVerification.rejected, (state, action) => {
        state.auditStatus.loading = false;
        state.auditStatus.error = action.payload;
        message.error(action.payload || '审核操作失败');
      });
  }
});

export const { clearAuditStatus, setSearchParams } = userVerificationSlice.actions;

export default userVerificationSlice.reducer; 